import { LoadingSpinner } from '../shared/components/LoadingSpinner';
import { emptyState } from '../shared/components/Narrative';
import { icon } from '../shared/components/icons';
import { mountSlackPeople } from '../shared/components/SlackSettings';   // R109 (l)
import { resolveRosterId, isActiveRow } from '../../../lib/rosterIdentity.mjs';   // R120: the login → roster row rule (C8)

// Users & Roles (PDF Notes-10 #13) — admin user management. List accounts,
// invite a new user by email + role (Supabase invite → magic link), and change
// a user's role / active state. Admin-invite only; no public signup. Backed by
// GET /api/users, POST /api/users/invite, PATCH /api/users/:id.
const ROLE_PILL: Record<string, string> = {
  admin: 'pill-purple', director: 'pill-blue', manager: 'pill-blue', remote_ops: 'pill-blue',
  maintenance: 'pill-amber', inspector: 'pill-amber', cleaner: 'pill-green', owner: 'pill-gray',
  accounting: 'pill-gray',   // R100 A1: external accounting partner
};

export class UsersPage {
  private container: HTMLElement;
  private users: any[] = [];
  private ownerLogins = 0;   // R117: counted, listed under Owner Operations instead
  private roles: string[] = [];
  private labels: Record<string, string> = {};
  private inviting = false;
  private note = '';
  // Result of the last "Resend invite" — a fresh link to copy + who it's for.
  private resend: { id: string; email: string; link?: string; msg: string; ok: boolean } | null = null;
  private owners: any[] = [];
  // R5 #4 — roster identities a field login can be linked to ("assigned to me").
  private rosters: { cleaner: any[]; vendor: any[]; inspector: any[] } = { cleaner: [], vendor: [], inspector: [] };
  // R120: which roster lists actually loaded — a failed read never reads as "removed".
  private rostersLoaded: { cleaner: boolean; vendor: boolean; inspector: boolean } = { cleaner: false, vendor: false, inspector: false };
  // R120 review fix (#9): GET /api/users said links_unavailable (tech_user_links unreadable).
  private linksUnavailable = false;
  // R120: per-row result of the last Relink / "+ Add to …" ({ id → message }).
  private rowNote: Record<string, { ok: boolean; msg: string }> = {};
  // R116 #2: the mobile number editor (one row at a time) — a phone on file puts the person on the
  // Text-vendor modal's group chips and names them inside a vendor group text.
  private phoneEdit: string | null = null;
  private phoneErr = '';
  private phoneSaving = false;

  constructor(container: HTMLElement) { this.container = container; }

  private esc(s: any): string { return String(s == null ? '' : s).replace(/[&<>"]/g, c => ({ '&': '&amp;', '<': '&lt;', '>': '&gt;', '"': '&quot;' }[c]!)); }
  // "last seen" formatting — actual sign-in activity, distinct from the
  // Active/Inactive switch (which only says whether sign-in is ALLOWED).
  private ago(iso: string): string {
    const t = new Date(iso).getTime();
    if (!Number.isFinite(t)) return '—';
    const mins = Math.floor((Date.now() - t) / 60000);
    if (mins < 1) return 'just now';
    if (mins < 60) return `${mins}m ago`;
    if (mins < 1440) return `${Math.floor(mins / 60)}h ago`;
    const days = Math.floor(mins / 1440);
    return days < 30 ? `${days}d ago` : new Date(t).toLocaleDateString();
  }
  private label(r: string): string { return this.labels[r] || r; }
  // Roster-identity <select> options grouped by kind (value = "kind|roster_id").
  private linkOptions(selected = ''): string {
    const grp = (label: string, kind: string, list: any[]) => list.length
      ? `<optgroup label="${label}">${list.map(x => { const v = `${kind}|${this.esc(x.id)}`; return `<option value="${v}" ${selected === v ? 'selected' : ''}>${this.esc(x.name)}</option>`; }).join('')}</optgroup>` : '';
    return `<option value="">— none —</option>${grp('Cleaners', 'cleaner', this.rosters.cleaner)}${grp('Vendors / Maintenance', 'vendor', this.rosters.vendor)}${grp('Inspectors', 'inspector', this.rosters.inspector)}`;
  }

  async render(): Promise<void> {
    this.container.innerHTML = `<div class="page-content"><div style="padding:48px; text-align:center;">${LoadingSpinner.render()}</div></div>`;
    await this.load();
    this.draw();
  }

  private async load(): Promise<void> {
    try {
      const r = await fetch('/api/users');
      if (r.status === 403) { this.users = []; this.roles = []; this.note = '__forbidden__'; return; }
      const j = await r.json();
      // R117 (Jesse: "I want the owner users to be separate from the internal users page"):
      // owner logins live with their owner (Owner Operations → Owners → the owner → Logins).
      this.ownerLogins = (j.users || []).filter((u: any) => u.role === 'owner').length;
      this.users = (j.users || []).filter((u: any) => u.role !== 'owner');
      // R120 review fix (#9): the roster links couldn't be read — every
      // tech_link is UNKNOWN (not "not linked"); the roster blocks say so.
      this.linksUnavailable = !!j.links_unavailable;
      this.roles = j.roles || [];
      this.labels = j.role_labels || {};
      try { const o = await fetch('/api/owners').then(r => r.ok ? r.json() : { owners: [] }); this.owners = o.owners || []; } catch (_) { /* */ }
      try {
        const [cl, ve, ma] = await Promise.all([
          fetch('/api/assetops/cleaners').then(r => r.ok ? r.json() : null).catch(() => null),
          fetch('/api/assetops/vendors').then(r => r.ok ? r.json() : null).catch(() => null),
          fetch('/api/assetops/inspections/managers').then(r => r.ok ? r.json() : null).catch(() => null),
        ]);
        // R120 review fix (#8): a list counts as loaded only when the answer
        // carries the list and no "unavailable" flag (the roster GETs answer
        // 503 on a failed read) — a failed read never reads as "removed".
        const listOf = (j: any, ...keys: string[]): any[] | null => {
          if (!j || j.roster_unavailable) return null;
          for (const k of keys) if (Array.isArray(j[k])) return j[k];
          return null;
        };
        const cList = listOf(cl, 'cleaners'), vList = listOf(ve, 'vendors'), mList = listOf(ma, 'managers', 'inspectors');
        this.rosters = {
          cleaner: cList || [],
          // R120: a removed vendor (archived) is never offered as a link target
          vendor: (vList || []).filter((v: any) => isActiveRow(v)),
          inspector: mList || [],
        };
        this.rostersLoaded = { cleaner: !!cList, vendor: !!vList, inspector: !!mList };
      } catch (_) { /* */ }
    } catch (_) { this.users = []; }
  }

  private draw(): void {
    if (this.note === '__forbidden__') {
      this.container.innerHTML = `<div class="page-content">${emptyState('Admins only', 'User management is restricted to Admin and Director roles.', '🔒')}</div>`;
      return;
    }
    const active = this.users.filter(u => u.is_active !== false).length;
    const owners = this.ownerLogins;   // R117: owner logins are managed under Owner Operations
    const kpi = (label: string, ico: string, value: string, cap: string, tone = '') => `
      <div class="kpi-card ${tone}">
        <div class="kpi-card-top"><span class="kpi-card-label">${label}</span><span class="kpi-card-ic">${icon(ico, 17)}</span></div>
        <div class="kpi-card-value">${value}</div>
        <div class="kpi-card-cap">${cap}</div>
      </div>`;

    const roleOpts = (sel: string) => this.roles.map(r => `<option value="${r}" ${r === sel ? 'selected' : ''}>${this.esc(this.label(r))}</option>`).join('');

    const rows = this.users.map(u => `
      <div class="pq-row us-row" style="cursor:default;">
        <span class="pq-badge" style="background:var(--bg-subtle); color:var(--text-muted); font-size:13px;">${this.esc((u.full_name || u.email || '?').slice(0, 2).toUpperCase())}</span>
        <div class="pq-main">
          <div class="pq-name">${this.esc(u.full_name || '—')}</div>
          <div style="font-size:11.5px; color:var(--text-muted); margin-top:2px;">${this.esc(u.email || '')} <a class="us-editmail" data-id="${this.esc(u.id)}" data-email="${this.esc(u.email || '')}" title="Change this login's email — a fresh sign-in link for the new address is minted automatically" style="cursor:pointer; color:var(--accent); font-size:11px;">✏️ change</a></div>
          ${this.phoneHtml(u)}
          ${u.last_active ? `<div style="font-size:10.5px; color:var(--text-faint); margin-top:1px;">last seen ${this.esc(this.ago(u.last_active))}</div>` : ''}
          ${this.rosterHtml(u)}
        </div>
        <div class="pq-right" style="gap:10px;">
          <span class="pill ${u.is_active === false ? 'pill-gray' : 'pill-green'}" title="Enabled/disabled switch — 'Active' means ALLOWED to sign in, not that they have. 'Pending' + 'last seen' show actual logins.">${u.is_active === false ? 'Inactive' : 'Active'}</span>
          ${u.invite_accepted === false ? '<span class="pill pill-amber" title="Invited but has never signed in">Pending</span>' : ''}
          ${u.approved === false ? `<span class="pill pill-red" title="The app refuses this login until an admin or director approves it (it was made outside the app, or its approval did not finish)">Not approved</span>${this.canApprove() ? `<button class="us-approve" data-id="${this.esc(u.id)}" data-email="${this.esc(u.email || '')}" style="font-size:11px; padding:4px 10px; border:1px solid var(--border); border-radius:7px; background:#fff; cursor:pointer; color:var(--accent); font-weight:600;">Approve</button>` : ''}` : ''}
          <span class="us-slack" data-email="${this.esc(String(u.email || '').toLowerCase())}"></span>
          <select class="us-role" data-id="${this.esc(u.id)}" style="padding:5px 9px; border:1px solid var(--border); border-radius:7px; font-size:12px; background:#fff; cursor:pointer;">${roleOpts(u.role)}</select>
          <label class="us-shared" title="Shared company login (e.g. a vendor's crew on one account) — the app asks who's working on each device and attributes work as 'Company — Person'" style="display:inline-flex; align-items:center; gap:4px; font-size:11px; color:var(--text-muted); cursor:pointer;"><input type="checkbox" data-shared-id="${this.esc(u.id)}" ${u.shared ? 'checked' : ''} style="accent-color:var(--accent);">Shared</label>
          <button class="us-viewas" data-id="${this.esc(u.id)}" title="Browse the app exactly as this person sees it — their role, their assigned work" style="font-size:11px; padding:4px 10px; border:1px solid var(--border); border-radius:7px; background:#fff; cursor:pointer; color:var(--text);">👁 View as</button>
          <button class="us-resend" data-id="${this.esc(u.id)}" data-email="${this.esc(u.email || '')}" title="Generate a fresh sign-in link you can send them" style="font-size:11px; padding:4px 10px; border:1px solid var(--border); border-radius:7px; background:#fff; cursor:pointer; color:var(--accent); font-weight:600;">Resend invite</button>
          <button class="us-toggle" data-id="${this.esc(u.id)}" data-active="${u.is_active === false ? '0' : '1'}" style="font-size:11px; padding:4px 10px; border:1px solid var(--border); border-radius:7px; background:#fff; cursor:pointer; color:var(--text-muted);">${u.is_active === false ? 'Reactivate' : 'Deactivate'}</button>
          <button class="us-delete" data-id="${this.esc(u.id)}" data-email="${this.esc(u.email || '')}" title="Delete this login permanently" style="font-size:11px; padding:4px 10px; border:1px solid var(--border); border-radius:7px; background:#fff; cursor:pointer; color:var(--danger, #dc2626);">🗑</button>
        </div>
      </div>${this.phoneEditHtml(u)}`).join('');

    this.container.innerHTML = `
      <div class="page-content">
        <style>
          /* R120: on a phone the account row wraps — name, email and the roster
             block take the full width and the controls flow underneath (the
             fixed-width control strip used to squeeze the text to one word a line). */
          @media (max-width: 640px) {
            .us-row { flex-wrap: wrap; align-items: flex-start; padding: 12px 16px; }
            .us-row .pq-main { flex: 1 1 calc(100% - 60px); }
            .us-row .pq-right { flex: 1 1 100%; flex-wrap: wrap; gap: 8px; }
            .us-row .us-relink { max-width: 100%; }
          }
        </style>
        <div class="dash-head">
          <h1>Users &amp; Roles</h1>
          <p>Invite teammates and set what each person can access. Admin-invite only — no public sign-up.${this.ownerLogins ? ` Owner logins (${this.ownerLogins}) live in <a href="#" id="us-owner-logins" style="color:var(--accent);">Owner Operations → Owners</a>.` : ' Owner logins live in Owner Operations → Owners.'}</p>
        </div>

        <div class="kpi-grid cols-3">
          ${kpi('Users', 'users', String(this.users.length), 'all accounts')}
          ${kpi('Active', 'heart', String(active), 'can sign in', active ? 'tone-good' : '')}
          ${kpi('Owners', 'home', String(owners), 'portal logins · managed in Owner Operations')}
        </div>

        <div class="pq-card" style="margin-top:18px;">
          <div class="pq-card-h"><div><h3>Add a user</h3><p>creates the account instantly and gives you a sign-in link to send them — no email limits, no waiting on delivery</p></div></div>
          <div style="display:flex; gap:10px; flex-wrap:wrap; align-items:flex-end; padding:16px 22px;">
            <div style="display:flex; flex-direction:column; gap:4px;">
              <label style="font-size:11px; font-weight:600; color:var(--text-muted); text-transform:uppercase; letter-spacing:.05em;">Email</label>
              <input id="us-email" type="email" placeholder="name@company.com" style="padding:8px 12px; border:1px solid var(--border); border-radius:8px; font-size:13px; min-width:240px;">
            </div>
            <div style="display:flex; flex-direction:column; gap:4px;">
              <label style="font-size:11px; font-weight:600; color:var(--text-muted); text-transform:uppercase; letter-spacing:.05em;">Name</label>
              <input id="us-name" type="text" placeholder="Full name" style="padding:8px 12px; border:1px solid var(--border); border-radius:8px; font-size:13px; min-width:180px;">
            </div>
            <div style="display:flex; flex-direction:column; gap:4px;">
              <label style="font-size:11px; font-weight:600; color:var(--text-muted); text-transform:uppercase; letter-spacing:.05em;">Role</label>
              <select id="us-invite-role" style="padding:8px 12px; border:1px solid var(--border); border-radius:8px; font-size:13px; background:#fff;">${roleOpts('cleaner')}</select>
            </div>
            <div style="display:flex; flex-direction:column; gap:4px;">
              <label style="font-size:11px; font-weight:600; color:var(--text-muted); text-transform:uppercase; letter-spacing:.05em;">Owner (owner role)</label>
              <select id="us-invite-owner" style="padding:8px 12px; border:1px solid var(--border); border-radius:8px; font-size:13px; background:#fff;"><option value="">— none —</option>${this.owners.map(o => `<option value="${this.esc(o.id)}">${this.esc(o.name)}</option>`).join('')}</select>
            </div>
            <div style="display:flex; flex-direction:column; gap:4px;">
              <label style="font-size:11px; font-weight:600; color:var(--text-muted); text-transform:uppercase; letter-spacing:.05em;" title="Links a field login to their roster identity so 'assigned to me' works">Roster identity (field roles)</label>
              <select id="us-invite-link" style="padding:8px 12px; border:1px solid var(--border); border-radius:8px; font-size:13px; background:#fff;">${this.linkOptions()}</select>
            </div>
            <button id="us-invite-btn" class="btn btn-primary" ${this.inviting ? 'disabled' : ''} style="padding:9px 18px; border-radius:8px; font-size:13px; font-weight:600; background:var(--accent); color:#fff; border:none; cursor:pointer;">${this.inviting ? 'Creating…' : 'Add user & get link'}</button>
          </div>
          ${this.note && this.note !== '__forbidden__' ? `<div style="padding:0 22px 14px; font-size:12.5px; color:${this.note.startsWith('✓') ? 'var(--success-text)' : 'var(--danger-text)'};">${this.esc(this.note)}</div>` : ''}
        </div>

        ${this.resend ? `
        <div class="pq-card" style="margin-top:18px; border:1px solid ${this.resend.ok ? 'var(--success-border, #b7e4c7)' : 'var(--danger-border, #f2c2c2)'};">
          <div style="padding:16px 22px; display:flex; flex-direction:column; gap:10px;">
            <div style="font-size:13px; font-weight:600; color:${this.resend.ok ? 'var(--success-text)' : 'var(--danger-text)'};">${this.esc(this.resend.msg)}</div>
            ${this.resend.link ? `
              <div style="display:flex; gap:8px; align-items:center;">
                <input id="us-resend-link" type="text" readonly value="${this.esc(this.resend.link)}" style="flex:1; padding:8px 12px; border:1px solid var(--border); border-radius:8px; font-size:12px; font-family:monospace; background:var(--bg-subtle); color:var(--text);">
                <button id="us-resend-copy" class="btn" style="padding:8px 16px; border-radius:8px; font-size:12px; font-weight:600; background:var(--accent); color:#fff; border:none; cursor:pointer; white-space:nowrap;">Copy link</button>
              </div>
              <div style="font-size:11.5px; color:var(--text-muted);">Send this link to ${this.esc(this.resend.email)} by text or email. It lets them set a password and sign in — no waiting on invite email delivery. <a href="#" id="us-resend-email" style="color:var(--accent); font-weight:600;">Or email it to them from Supabase instead →</a></div>
            ` : ''}
          </div>
        </div>` : ''}

        ${(() => {
          // view-as is honored only for admin/director (main.ts guard) — don't
          // show managers a button that silently does nothing.
          try {
            const r = sessionStorage.getItem('realUserRole') || sessionStorage.getItem('userRole') || '';
            if (r !== 'admin' && r !== 'director') return '';
          } catch (_) { return ''; }
          return `
        <div class="pq-card" style="margin-top:18px;">
          <div class="pq-card-h"><div><h3>View the app as…</h3><p>browse every page the way another role sees it — your real permissions stay yours; a banner shows until you exit (R13)</p></div></div>
          <div style="display:flex; gap:10px; align-items:center; padding:16px 22px;">
            <select id="us-viewas-role" style="padding:8px 12px; border:1px solid var(--border); border-radius:8px; font-size:13px; background:#fff;">
              ${this.roles.filter(r => r !== 'admin').map(r => `<option value="${r}">${this.esc(this.label(r))}</option>`).join('')}
            </select>
            <button id="us-viewas-btn" class="btn" style="padding:9px 18px; border-radius:8px; font-size:13px; font-weight:600; background:var(--text, #0f172a); color:#fff; border:none; cursor:pointer;">👁 Start viewing as</button>
            <span style="font-size:11.5px; color:var(--text-muted);">Tip: field pages (cleaner/tech) show "assigned to me" work only when that role has a roster identity — link one above if you want realistic data.</span>
          </div>
        </div>`;
        })()}

        <div class="pq-card" style="margin-top:18px;">
          <div class="pq-card-h"><div><h3>Accounts</h3><p>${this.users.length} ${this.users.length === 1 ? 'user' : 'users'} — change a role, resend an invite, or delete a login</p></div></div>
          ${rows || '<div style="text-align:center; color:var(--text-muted); padding:24px; font-size:13px;">No users yet.</div>'}
        </div>
        <div id="us-slack-card"></div>
      </div>`;
    this.bind();
    void mountSlackPeople(this.container);   // R109 (l): Slack column + matching card (best-effort, async)
  }

  // R120 (team: an invited cleaner "can't see her assigned cleans" — her login
  // pointed at a roster row the office had removed; Lacey cleans AND inspects):
  // per field-role login, WHICH roster row it works as and on which rosters it
  // is assignable. The link state uses the cleaner portal's own rule
  // (lib/rosterIdentity resolveRosterId): a link to a removed row is flagged
  // with a Relink select (POST /api/users/:id/tech-link); "+ Add to Cleaners /
  // Inspectors" puts the login on that roster (the roster routes, by email).
  private static readonly FIELD_ROLES = ['cleaner', 'inspector', 'maintenance'];
  private static readonly KIND_FOR_ROLE: Record<string, 'cleaner' | 'inspector' | 'vendor'> = { cleaner: 'cleaner', inspector: 'inspector', maintenance: 'vendor' };
  private static readonly KIND_LABEL: Record<string, string> = { cleaner: 'Cleaners', inspector: 'Inspectors', vendor: 'Vendors' };
  private onRoster(u: any, kind: 'cleaner' | 'inspector' | 'vendor'): any | null {
    const r = resolveRosterId({ link: u.tech_link || null, kind, rows: this.rosters[kind], email: u.email || '', rowsLoaded: this.rostersLoaded[kind] });
    return r.id ? (this.rosters[kind].find((x: any) => String(x.id) === r.id) || { id: r.id, name: '' }) : null;
  }
  private rosterHtml(u: any): string {
    if (!UsersPage.FIELD_ROLES.includes(u.role)) return '';
    const id = this.esc(u.id);
    // R120 review fix (#9): with the links unknown, "not on the roster" and a
    // "+ Add" would be a guess (one click made a twin row). Say what failed,
    // offer nothing that writes.
    if (this.linksUnavailable) {
      return `<div class="us-roster" data-us-roster="${id}" style="margin-top:6px; font-size:11.5px; line-height:1.45;">
        <span data-us-linkstate="unknown" style="color:var(--text-faint);">🔗 Roster: couldn't load the roster links. Reload the page in a moment.</span>
      </div>`;
    }
    const link = u.tech_link || null;
    const kind = (link && ['cleaner', 'inspector', 'vendor'].includes(link.kind)) ? link.kind : UsersPage.KIND_FOR_ROLE[u.role];
    const res = resolveRosterId({ link, kind, rows: this.rosters[kind as 'cleaner'], email: u.email || '', rowsLoaded: this.rostersLoaded[kind as 'cleaner'] });
    const rowName = (rid: string | null) => { const r = rid ? this.rosters[kind as 'cleaner'].find((x: any) => String(x.id) === rid) : null; return r ? String(r.name || '') : ''; };
    const label = UsersPage.KIND_LABEL[kind] || kind;
    let state = '';
    if (!this.rostersLoaded[kind as 'cleaner']) {
      state = `<span style="color:var(--text-faint);">🔗 Roster: couldn't load the ${this.esc(label)} list</span>`;
    } else if (res.via === 'link') {
      state = `<span data-us-linkstate="ok" style="color:var(--success-text);">🔗 Works as <b>${this.esc(rowName(res.id) || res.id)}</b> on ${this.esc(label)}</span>`;
    } else if (res.stale_link) {
      state = `<span data-us-linkstate="stale" style="color:var(--warning-text); font-weight:600;">⚠ Linked to a ${this.esc(label.toLowerCase().replace(/s$/, ''))} profile that was removed${res.id ? ` — using ${this.esc(rowName(res.id))} (same email) until you relink` : ' — relink it below'}</span>`;
    } else if (res.via === 'email') {
      state = `<span data-us-linkstate="email" style="color:var(--text-muted);">🔗 Matched by email to <b>${this.esc(rowName(res.id))}</b> on ${this.esc(label)}${res.ambiguous ? ' (more than one has this email)' : ''}</span>`;
    } else {
      state = `<span data-us-linkstate="none" style="color:var(--warning-text);">🔗 Not on the ${this.esc(label)} roster — no assigned work will show for this login</span>`;
    }
    const sel = link ? `${this.esc(link.kind)}|${this.esc(link.roster_id)}` : '';
    // the stored link's target is not among the listed (active) rows → keep it visible as what it is
    const listed = !!link && (this.rosters[link.kind as 'cleaner'] || []).some((x: any) => String(x.id) === String(link.roster_id));
    // R120 review fix (#8): when the link's roster didn't load, the select keeps
    // the CURRENT link as its value (never shows "— none —" as if unlinked).
    const staleOpt = !link || listed ? ''
      : this.rostersLoaded[link.kind as 'cleaner'] ? `<option value="${sel}" selected disabled>⚠ removed profile — pick one</option>`
      : `<option value="${sel}" selected disabled>Current profile (list didn't load)</option>`;
    const onC = this.onRoster(u, 'cleaner');
    const onI = u.role === 'inspector' ? true : this.onRoster(u, 'inspector');
    const also = [onC ? 'Housekeeping (on Cleaners roster)' : '', onI ? 'Inspections (on Inspectors roster)' : ''].filter(Boolean);
    const addBtn = (k: 'cleaner' | 'inspector') => `<button type="button" class="us-addroster" data-id="${id}" data-kind="${k}" style="min-height:36px; font-size:11.5px; padding:4px 10px; border:1px solid var(--border); border-radius:7px; background:var(--card, #fff); cursor:pointer; color:var(--accent); font-weight:600;">+ Add to ${k === 'cleaner' ? 'Cleaners' : 'Inspectors'}</button>`;
    const adds = [this.rostersLoaded.cleaner && !onC ? addBtn('cleaner') : '', this.rostersLoaded.inspector && !onI ? addBtn('inspector') : ''].filter(Boolean).join(' ');
    const note = this.rowNote[u.id];
    return `<div class="us-roster" data-us-roster="${id}" style="margin-top:6px; font-size:11.5px; line-height:1.45; display:flex; flex-direction:column; gap:4px;">
      <div>${state}</div>
      <div style="display:flex; gap:6px; align-items:center; flex-wrap:wrap;">
        <label style="display:inline-flex; gap:6px; align-items:center; color:var(--text-muted);">Relink
          <select class="us-relink" data-id="${id}" data-cur="${sel}" aria-label="Roster profile this login works as" style="min-height:36px; max-width:220px; padding:4px 8px; border:1px solid var(--border); border-radius:7px; font-size:12px; background:var(--card, #fff);">${staleOpt}${this.linkOptions(staleOpt ? '' : sel)}</select>
        </label>
        ${adds}
      </div>
      ${also.length ? `<div style="color:var(--text-muted);">Also assignable for: ${this.esc(also.join(' · '))}</div>` : ''}
      ${note ? `<div class="us-rownote" style="color:${note.ok ? 'var(--success-text)' : 'var(--danger-text)'};">${this.esc(note.msg)}</div>` : ''}
    </div>`;
  }
  private async relink(id: string, value: string, sel: HTMLSelectElement): Promise<void> {
    const [kind, roster_id] = value ? value.split('|') : ['', ''];
    if (!roster_id && !confirm('Remove this login\'s roster link? Their own view then finds work only by matching their email.')) { sel.value = sel.dataset.cur || ''; return; }
    sel.disabled = true;
    try {
      const r = await fetch(`/api/users/${encodeURIComponent(id)}/tech-link`, { method: 'POST', headers: { 'Content-Type': 'application/json' }, body: JSON.stringify(roster_id ? { kind, roster_id } : {}) });
      const j = await r.json().catch(() => ({}));
      if (!r.ok) { this.rowNote[id] = { ok: false, msg: `Couldn't relink — ${j.error || r.status}` }; sel.value = sel.dataset.cur || ''; sel.disabled = false; this.draw(); return; }
      const row = this.users.find(u => u.id === id);
      if (row) row.tech_link = j.link || null;
      this.rowNote[id] = { ok: true, msg: j.link ? '✓ Relinked — their own view uses this profile from their next page load.' : '✓ Link removed.' };
    } catch (e: any) { this.rowNote[id] = { ok: false, msg: `Couldn't relink — ${e.message || 'no connection'}` }; sel.value = sel.dataset.cur || ''; }
    this.draw();
  }
  private async addToRoster(id: string, kind: 'cleaner' | 'inspector', btn: HTMLButtonElement): Promise<void> {
    const u = this.users.find(x => x.id === id);
    if (!u) return;
    const name = String(u.full_name || '').trim();
    if (!name) { this.rowNote[id] = { ok: false, msg: 'Give this login a name first — the roster lists people by name.' }; this.draw(); return; }
    btn.disabled = true;
    const url = kind === 'cleaner' ? '/api/assetops/cleaners' : '/api/assetops/inspections/managers';
    try {
      const r = await fetch(url, { method: 'POST', headers: { 'Content-Type': 'application/json' }, body: JSON.stringify({ name, email: u.email || '' }) });
      const j = await r.json().catch(() => ({}));
      if (r.status === 409 && j.code === 'DUP_EMAIL') this.rowNote[id] = { ok: false, msg: `Already on that roster — as ${j.existing_name || 'another profile'} (same email).` };
      // R120 review fix (#9): the server refuses a twin for a login that already works as a row
      else if (r.status === 409 && j.code === 'DUP_LOGIN') this.rowNote[id] = { ok: false, msg: `Already on that roster: this login works as ${j.existing_name || 'another profile'}.` };
      else if (!r.ok) this.rowNote[id] = { ok: false, msg: `Couldn't add — ${j.error || r.status}` };
      else this.rowNote[id] = { ok: true, msg: `✓ Added to ${kind === 'cleaner' ? 'Cleaners' : 'Inspectors'} as ${name}.` };
    } catch (e: any) { this.rowNote[id] = { ok: false, msg: `Couldn't add — ${e.message || 'no connection'}` }; }
    await this.load(); this.draw();
  }

  // R116 #2: "📱 (619) 555-0101 ✏️" / "📱 Add mobile" — inline editor, E.164 on the server.
  private fmtPhone(p: string): string {
    const m = /^\+1(\d{3})(\d{3})(\d{4})$/.exec(String(p || ''));
    return m ? `(${m[1]}) ${m[2]}-${m[3]}` : String(p || '');
  }
  private phoneHtml(u: any): string {
    const id = this.esc(u.id);
    if (this.phoneEdit === u.id) return `<div style="font-size:11.5px; color:var(--accent); margin-top:2px; font-weight:600;">📱 editing below…</div>`;
    return `<div style="font-size:11.5px; color:var(--text-muted); margin-top:2px;">📱 ${u.phone ? `${this.esc(this.fmtPhone(u.phone))} <a class="us-phone" data-id="${id}" title="Change this person's mobile number" style="cursor:pointer; color:var(--accent); font-size:11px;">✏️ change</a>` : `<a class="us-phone" data-id="${id}" title="Add a mobile so they can be put on vendor group texts" style="cursor:pointer; color:var(--accent); font-size:11px; font-weight:600;">Add mobile</a>`}</div>`;
  }
  /** The editor is a full-width strip under the row (the row's middle column is too narrow on a phone). */
  private phoneEditHtml(u: any): string {
    if (this.phoneEdit !== u.id) return '';
    const id = this.esc(u.id);
    return `<div class="us-phone-edit" style="display:flex; gap:8px; align-items:center; flex-wrap:wrap; padding:12px 22px 14px; border-bottom:1px solid var(--border-soft); background:var(--bg-subtle);">
        <label for="us-phone-${id}" style="flex-basis:100%; font-size:11px; font-weight:600; color:var(--text-muted); text-transform:uppercase; letter-spacing:.05em;">Mobile for ${this.esc(u.full_name || u.email || 'this user')}</label>
        <input id="us-phone-${id}" type="tel" inputmode="tel" autocomplete="off" class="input us-phone-input" data-id="${id}" value="${this.esc(u.phone ? this.fmtPhone(u.phone) : '')}" placeholder="(619) 555-0123" style="flex:1 1 180px; max-width:260px; min-height:38px;">
        <button type="button" class="btn btn-primary us-phone-save" data-id="${id}" style="min-height:38px;" ${this.phoneSaving ? 'disabled' : ''}>${this.phoneSaving ? 'Saving…' : 'Save'}</button>
        <button type="button" class="btn btn-ghost us-phone-cancel" style="min-height:38px;">Cancel</button>
        ${u.phone ? `<button type="button" class="btn btn-ghost us-phone-clear" data-id="${id}" style="min-height:38px; color:var(--danger-text);">Remove</button>` : ''}
        <div style="flex-basis:100%; font-size:11.5px; line-height:1.45; color:${this.phoneErr ? 'var(--danger-text)' : 'var(--text-faint)'};" ${this.phoneErr ? 'role="alert"' : ''}>${this.phoneErr ? this.esc(this.phoneErr) : 'Puts them on vendor group texts (Maintenance → 📲 Text vendor). Everyone in a group text sees each other\'s number.'}</div>
      </div>`;
  }
  private async savePhone(id: string, phone: string): Promise<void> {
    if (this.phoneSaving) return;
    this.phoneSaving = true; this.phoneErr = ''; this.draw();
    try {
      const r = await fetch(`/api/users/${encodeURIComponent(id)}`, { method: 'PATCH', headers: { 'Content-Type': 'application/json' }, body: JSON.stringify({ phone }) });
      const j = await r.json().catch(() => ({}));
      this.phoneSaving = false;
      if (!r.ok) { this.phoneErr = j.error || `Couldn't save (HTTP ${r.status})`; this.draw(); return; }
      const row = this.users.find(u => u.id === id);
      if (row) row.phone = j.phone === undefined ? (phone || null) : j.phone;
      this.phoneEdit = null; this.phoneErr = '';
      this.draw();
    } catch (e: any) { this.phoneSaving = false; this.phoneErr = `Couldn't save — ${e.message || 'no connection'}`; this.draw(); }
  }

  private bind(): void {
    this.container.querySelectorAll<HTMLElement>('.us-phone').forEach(a => a.addEventListener('click', () => {
      this.phoneEdit = a.dataset.id || null; this.phoneErr = ''; this.draw();
      this.container.querySelector<HTMLInputElement>('.us-phone-input')?.focus();
    }));
    this.container.querySelector<HTMLButtonElement>('.us-phone-cancel')?.addEventListener('click', () => { this.phoneEdit = null; this.phoneErr = ''; this.draw(); });
    this.container.querySelector<HTMLButtonElement>('.us-phone-save')?.addEventListener('click', (e) => {
      const id = (e.currentTarget as HTMLElement).dataset.id || '';
      const v = (this.container.querySelector<HTMLInputElement>('.us-phone-input') || { value: '' }).value.trim();
      if (!v) { this.phoneErr = 'Type the mobile number, or use Remove.'; this.draw(); return; }
      void this.savePhone(id, v);
    });
    this.container.querySelector<HTMLInputElement>('.us-phone-input')?.addEventListener('keydown', (e) => {
      if (e.key === 'Enter') { e.preventDefault(); this.container.querySelector<HTMLButtonElement>('.us-phone-save')?.click(); }
      if (e.key === 'Escape') { this.phoneEdit = null; this.phoneErr = ''; this.draw(); }
    });
    this.container.querySelector<HTMLButtonElement>('.us-phone-clear')?.addEventListener('click', (e) => {
      const id = (e.currentTarget as HTMLElement).dataset.id || '';
      if (!confirm('Remove this mobile number? They drop off the vendor group-text chips.')) return;
      void this.savePhone(id, '');
    });
    this.container.querySelectorAll<HTMLSelectElement>('.us-role').forEach(sel =>
      sel.addEventListener('change', async () => {
        // R46: role changes swap the person's ENTIRE app (a manager set to
        // "inspector" loses the office view — this happened to a real manager).
        // Confirm, and snap the select back on cancel.
        const row = this.users.find(u => u.id === sel.dataset.id);
        const prior = row?.role || '';
        if (!confirm(`Change this person's role to "${sel.value}"? Their app view changes immediately — field roles (cleaner/inspector/maintenance) lose all office pages. To make a manager assignable for inspections, keep them as manager: managers now appear in inspector dropdowns. To let a housekeeper also inspect, keep their role and use "+ Add to Inspectors" on their row.`)) {
          if (prior) sel.value = prior;
          return;
        }
        // Keep the local roster truthful so a later Cancel snaps back to the
        // CURRENT server role, not a stale pre-change one.
        if (await this.patch(sel.dataset.id!, { role: sel.value })) {
          if (row) row.role = sel.value;
        } else if (prior) {
          sel.value = prior;
        }
      }));
    // R30 #5: shared-company-login flag (Marina, Fido) — flips the per-device
    // "Who's working?" prompt on for this account.
    this.container.querySelectorAll<HTMLInputElement>('[data-shared-id]').forEach(cb =>
      cb.addEventListener('change', async () => {
        try {
          const r = await fetch(`/api/users/${encodeURIComponent(cb.dataset.sharedId!)}/shared-flag`, {
            method: 'POST', headers: { 'Content-Type': 'application/json' },
            body: JSON.stringify({ shared: cb.checked }),
          });
          if (!r.ok) throw new Error(`HTTP ${r.status}`);
        } catch (e: any) { alert(`Could not update shared flag — ${e.message}`); cb.checked = !cb.checked; }
      }));
    this.container.querySelectorAll<HTMLButtonElement>('.us-toggle').forEach(btn =>
      btn.addEventListener('click', async () => {
        await this.patch(btn.dataset.id!, { is_active: btn.dataset.active !== '1' });
        await this.load(); this.draw();
      }));
    // R116 security: let a login made outside the app past the sign-up gate.
    this.container.querySelectorAll<HTMLButtonElement>('.us-approve').forEach(btn =>
      btn.addEventListener('click', async () => {
        if (!confirm(`Approve ${btn.dataset.email || 'this login'}?\n\nCheck its role first — they get exactly what that role allows.`)) return;
        await this.patch(btn.dataset.id!, { approve: true });
        await this.load(); this.draw();
      }));
    this.container.querySelectorAll<HTMLButtonElement>('.us-resend').forEach(btn =>
      btn.addEventListener('click', () => this.resendInvite(btn.dataset.id!, btn.dataset.email || '', 'link', btn)));
    this.container.querySelectorAll<HTMLButtonElement>('.us-delete').forEach(btn =>
      btn.addEventListener('click', async () => {
        const email = btn.dataset.email || 'this user';
        if (!confirm(`Delete ${email}'s login permanently?\n\nThey immediately lose access; their roster identity link is removed too. Work they logged (turns, orders, tasks) is kept. This can't be undone.`)) return;
        btn.disabled = true; btn.textContent = '…';
        try {
          const r = await fetch(`/api/users/${encodeURIComponent(btn.dataset.id!)}`, { method: 'DELETE' });
          const j = await r.json();
          if (!r.ok) throw new Error(j.error || 'delete failed');
          this.note = `✓ ${email} deleted.`;
          await this.load(); this.draw();
        } catch (e: any) {
          btn.disabled = false; btn.textContent = '🗑';
          this.note = `Delete failed: ${e.message}`;
          this.draw();
        }
      }));
    this.container.querySelector<HTMLButtonElement>('#us-invite-btn')?.addEventListener('click', () => this.invite());
    // R120: per-login Relink + "+ Add to Cleaners / Inspectors"
    this.container.querySelectorAll<HTMLSelectElement>('.us-relink').forEach(sel =>
      sel.addEventListener('change', () => { void this.relink(sel.dataset.id || '', sel.value, sel); }));
    this.container.querySelectorAll<HTMLButtonElement>('.us-addroster').forEach(btn =>
      btn.addEventListener('click', () => { void this.addToRoster(btn.dataset.id || '', btn.dataset.kind === 'inspector' ? 'inspector' : 'cleaner', btn); }));
    // R117: owner logins live with the owner
    this.container.querySelector<HTMLAnchorElement>('#us-owner-logins')?.addEventListener('click', (e) => {
      e.preventDefault();
      try { sessionStorage.setItem('hub_tab', 'owners'); } catch (_) { /* */ }
      document.dispatchEvent(new CustomEvent('app:navigate', { detail: 'owner-ops' }));
    });
    this.container.querySelector<HTMLButtonElement>('#us-viewas-btn')?.addEventListener('click', () => {
      const role = (this.container.querySelector('#us-viewas-role') as HTMLSelectElement)?.value;
      if (!role) return;
      try { sessionStorage.removeItem('viewAsUserId'); sessionStorage.setItem('viewAsRole', role); } catch (_) { /* */ }
      window.location.reload();
    });
    // Change a login's email (R14 #3): update auth + profile, then immediately
    // mint a fresh sign-in link for the NEW address into the copy banner.
    this.container.querySelectorAll<HTMLElement>('.us-editmail').forEach(a =>
      a.addEventListener('click', async () => {
        const id = a.dataset.id!;
        const oldEmail = a.dataset.email || '';
        const next = (window.prompt(`New email for ${oldEmail || 'this user'}:`, oldEmail) || '').trim();
        if (!next || next === oldEmail) return;
        try {
          const r = await fetch(`/api/users/${encodeURIComponent(id)}`, {
            method: 'PATCH', headers: { 'Content-Type': 'application/json' },
            body: JSON.stringify({ email: next }),
          });
          const j = await r.json();
          if (!r.ok) throw new Error(j.error || 'email change failed');
          await this.load();
          this.draw();
          // Fresh link for the new address so they can sign in right away.
          await this.resendInvite(id, next, 'link');
        } catch (e: any) {
          this.note = `Email change failed: ${e.message}`;
          this.draw();
        }
      }));

    // View as a SPECIFIC person (R14) — role + their roster identity, so
    // "assign a clean, then see it as them" actually works.
    this.container.querySelectorAll<HTMLButtonElement>('.us-viewas').forEach(btn =>
      btn.addEventListener('click', () => {
        try {
          const real = sessionStorage.getItem('realUserRole') || sessionStorage.getItem('userRole') || '';
          if (real !== 'admin' && real !== 'director') { this.note = 'View-as is limited to Admin and Director.'; this.draw(); return; }
          sessionStorage.removeItem('viewAsRole');
          sessionStorage.setItem('viewAsUserId', btn.dataset.id!);
        } catch (_) { /* */ }
        window.location.reload();
      }));
    const copyBtn = this.container.querySelector<HTMLButtonElement>('#us-resend-copy');
    if (copyBtn) copyBtn.addEventListener('click', async () => {
      const input = this.container.querySelector<HTMLInputElement>('#us-resend-link');
      if (!input) return;
      try { await navigator.clipboard.writeText(input.value); } catch (_) { input.select(); document.execCommand('copy'); }
      copyBtn.textContent = 'Copied ✓';
      setTimeout(() => { copyBtn.textContent = 'Copy link'; }, 1600);
    });
    this.container.querySelector<HTMLAnchorElement>('#us-resend-email')?.addEventListener('click', (e) => {
      e.preventDefault();
      if (this.resend) this.resendInvite(this.resend.id, this.resend.email, 'email');
    });
  }

  private async resendInvite(id: string, email: string, mode: 'link' | 'email', btn?: HTMLButtonElement): Promise<void> {
    const orig = btn?.textContent; if (btn) { btn.disabled = true; btn.textContent = 'Sending…'; }
    try {
      const r = await fetch(`/api/users/${encodeURIComponent(id)}/resend-invite`, {
        method: 'POST', headers: { 'Content-Type': 'application/json' }, body: JSON.stringify({ mode }),
      });
      const j = await r.json().catch(() => ({}));
      if (r.ok) {
        // R116: the server mints for the login's REAL sign-in email and says so when it differs from the profile's.
        const heads = j.warning ? ` ⚠ ${j.warning}` : '';
        this.resend = j.link
          ? { id, email: j.email || email, link: j.link, ok: true, msg: `✓ Fresh sign-in link ready for ${j.email || email} — copied to your clipboard${heads}` }
          : { id, email: j.email || email, ok: true, msg: `✓ Invite email sent to ${j.email || email}${heads}` };
      } else {
        this.resend = { id, email, ok: false, msg: `Couldn't resend: ${j.error || r.status}` };
      }
    } catch (e: any) {
      this.resend = { id, email, ok: false, msg: `Couldn't resend: ${e.message}` };
    }
    if (btn) { btn.disabled = false; btn.textContent = orig || 'Resend invite'; }
    this.draw();
    // Auto-copy the link so it's on the clipboard the instant it appears.
    if (this.resend?.link) {
      try { await navigator.clipboard.writeText(this.resend.link); const cb = this.container.querySelector<HTMLButtonElement>('#us-resend-copy'); if (cb) { cb.textContent = 'Copied ✓'; setTimeout(() => { cb.textContent = 'Copy link'; }, 1600); } } catch (_) { /* user can click Copy */ }
    }
    this.container.querySelector('.pq-card')?.scrollIntoView({ behavior: 'smooth', block: 'nearest' });
  }

  // R116: only an admin / director may approve (the server refuses everyone else).
  private canApprove(): boolean {
    let r = '';
    try { r = sessionStorage.getItem('userBaseRole') || sessionStorage.getItem('userRole') || ''; } catch (_) { r = ''; }
    return r === 'admin' || r === 'director';
  }

  private async patch(id: string, body: any): Promise<boolean> {
    try {
      const r = await fetch(`/api/users/${encodeURIComponent(id)}`, {
        method: 'PATCH', headers: { 'Content-Type': 'application/json' }, body: JSON.stringify(body),
      });
      if (!r.ok) { const j = await r.json().catch(() => ({})); this.note = `Update failed: ${j.error || r.status}`; this.draw(); return false; }
      return true;
    } catch (e: any) { this.note = `Update failed: ${e.message}`; this.draw(); return false; }
  }

  private async invite(): Promise<void> {
    const email = (this.container.querySelector('#us-email') as HTMLInputElement)?.value.trim();
    const full_name = (this.container.querySelector('#us-name') as HTMLInputElement)?.value.trim();
    const role = (this.container.querySelector('#us-invite-role') as HTMLSelectElement)?.value;
    const owner_id = (this.container.querySelector('#us-invite-owner') as HTMLSelectElement)?.value || null;
    const linkRaw = (this.container.querySelector('#us-invite-link') as HTMLSelectElement)?.value || '';
    let link: any = null;
    if (linkRaw && ['cleaner', 'maintenance', 'inspector'].includes(role)) {
      const [kind, roster_id] = linkRaw.split('|');
      if (kind && roster_id) link = { kind, roster_id };
    }
    if (!email) { this.note = 'Enter an email address.'; this.draw(); return; }
    this.inviting = true; this.note = ''; this.draw();
    try {
      const r = await fetch('/api/users/invite', {
        method: 'POST', headers: { 'Content-Type': 'application/json' }, body: JSON.stringify({ email, full_name, role, owner_id: role === 'owner' ? owner_id : null, link }),
      });
      const j = await r.json();
      this.inviting = false;
      if (r.ok) {
        // Account created without any email (rate-limit-proof). Show the sign-in
        // link in the same copyable banner the "Resend invite" flow uses.
        this.note = '';
        // R120: say which roster row the login now works as — nothing said one
        // had been made, so the office added the person again by hand (a twin).
        const KL: Record<string, string> = { cleaner: 'Cleaners', inspector: 'Inspectors', vendor: 'Vendors' };
        const ro = j.roster && j.roster.id
          ? ` · ${j.roster.state === 'matched' ? 'linked to' : j.roster.state === 'reactivated' ? 'brought back on' : 'added to'} ${KL[j.roster.kind] || 'the roster'}${j.roster.name ? ` as ${j.roster.name}` : ''}`
          : '';
        this.resend = j.link
          ? { id: j.id, email: j.email || email, link: j.link, ok: true, msg: `✓ ${email} added as ${this.label(role)}${ro} — their sign-in link is ready (copied to your clipboard)` }
          : { id: j.id, email: j.email || email, ok: true, msg: `✓ ${email} added as ${this.label(role)}${ro} — click "Resend invite" on their row to get a sign-in link.` };
      } else {
        this.note = `Invite failed: ${j.error || r.status}`;
      }
      await this.load();
      // R114 fix (data-safety #4): the account exists, but a link step (owner
      // portal / roster) failed — say so instead of dropping the warning.
      if (r.ok && (j.link_warning || j.roster_warning)) this.note = [j.link_warning, j.roster_warning].filter(Boolean).map(String).join(' ');
      this.draw();
      if (r.ok && this.resend?.link) {
        try { await navigator.clipboard.writeText(this.resend.link); } catch (_) { /* Copy button remains */ }
      }
    } catch (e: any) { this.inviting = false; this.note = `Invite failed: ${e.message}`; this.draw(); }
  }
}
